
// RealtorOfficeAddressUpsert.tsx — single parent for Add + Edit (Office)
// - Uses your shared <AddressForm /> for the return UI
// - Add mode: POST /addresses/user/:officeId
// - Edit mode: PUT  /addresses/:addressId
// - Default phone fallback: "0000000000"

import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AddressForm, { AddressValues } from "components/AddEditAddresses";
import { showError, showSuccess } from "toast/toast";
import {
  addressLine1Validation,
  cityValidation,
  countryValidation,
  firstNameValidation,
  lastNameValidation,
  phoneValidation,
  stateValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";

export default function RealtorAddEditOfficeAddresses() {
  // Expect routes like:
  //  - /realtor/office/:officeId/address/add
  //  - /realtor/office/:officeId/address/:addressId/edit
  const { officeId, addressId } = useParams<{ officeId?: string; addressId?: string }>();

  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";
  const apiBase = process.env.REACT_APP_API_URL as string;
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

  const [loading, setLoading] = useState<boolean>(!!addressId);
  const [initial, setInitial] = useState<Partial<AddressValues>>({});

  // EDIT: fetch existing address
  useEffect(() => {
    if (!addressId) return;

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${apiBase}/addresses/${addressId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const result = await res.json();
        if (!res.ok || (result && result.error)) {
          const message = result?.error || result?.message || "Error fetching address";
          showError(message);
          throw new Error(message);
        }

        setInitial({
          label_of_address: result?.label || "",
          firstName: result?.first_name || "",
          lastName: result?.last_name || "",
          company: result?.company || "",
          addressLine1: result?.address_details?.addressLine1 || "",
          addressLine2: result?.address_details?.addressLine2 || "",
          city: result?.address_details?.city || "",
          state: result?.address_details?.state || "",
          zipcode: result?.address_details?.zip || "",
          country: result?.address_details?.country || "",
          phone: result?.address_details?.phone || "",
          latitude: typeof result?.address_details?.latitude === "number" ? result?.address_details?.latitude : 0,
          longitude: typeof result?.address_details?.longitude === "number" ? result?.address_details?.longitude : 0,
          cityCode: result?.address_details?.cityCode || "",
          stateCode: result?.address_details?.stateCode || "",
          countryCode: result?.address_details?.countryCode || "",
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [addressId, apiBase, token]);

  const initialValues: AddressValues = useMemo(
    () => ({
      label_of_address: initial.label_of_address || "",
      firstName: initial.firstName || "",
      lastName: initial.lastName || "",
      company: initial.company || "",
      addressLine1: initial.addressLine1 || "",
      addressLine2: initial.addressLine2 || "",
      city: initial.city || "",
      state: initial.state || "",
      zipcode: initial.zipcode || "",
      country: initial.country || "",
      phone: initial.phone || "",
      cityCode: initial.cityCode || "",
      stateCode: initial.stateCode || "",
      countryCode: initial.countryCode || "",
      latitude: typeof initial.latitude === "number" ? initial.latitude : 0,
      longitude: typeof initial.longitude === "number" ? initial.longitude : 0,
    }),
    [initial]
  );

  const schema = Yup.object({
    label_of_address: Yup.string().required("Label of address is required"),
    firstName: firstNameValidation,
    lastName: lastNameValidation,
    addressLine1: addressLine1Validation,
    city: cityValidation,
    state: stateValidation,
    zipcode: zipcodeValidation,
    country: countryValidation,
    phone: phoneValidation,
  });

  if (loading) {
    return <div className="d-flex justify-content-center py-5">Loading...</div>;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          const isEdit = !!addressId;

          const url = isEdit
            ? `${apiBase}/addresses/${addressId}`
            : `${apiBase}/addresses/user/${officeId}`; // add under office

          const method = isEdit ? "PUT" : "POST";

          const payload = isEdit
            ? {
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              user_type: "office",
              // user_type: isTeammember ? "BROKERAGE_TEAM_MEMBER" : "BROKERAGE_FIRM",
              address_details: {
                label: values.label_of_address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                phone: (values.phone ?? "").toString().trim() || "0000000000",
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            }
            : {
              user_id: officeId, // attach to office
              user_type: "office",
              // user_type: isTeammember ? "BROKERAGE_TEAM_MEMBER" : "BROKERAGE_FIRM",
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              address_details: {
                label: values.label_of_address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                phone: (values.phone ?? "").toString().trim() || "0000000000",
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            };

          const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload),
          });

          const result = await res.json().catch(() => ({}));
          if (!res.ok || (result && result.error)) {
            const message = result?.error || result?.message || `Failed to ${isEdit ? "update" : "add"} address`;
            showError(message);
            throw new Error(message);
          }

          showSuccess(isEdit ? "Address updated successfully!" : "Address added successfully!");
          if (officeId) navigate(`${baseRoute}/view-office/${officeId}`);
          else navigate(`${baseRoute}/addresses`);
        } catch (err: any) {
          console.error(err);
          showError(err?.message || "Something went wrong");
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <div className="content">
          <div className="col-md-12 d-flex justify-content-center mt-4">
            <div className="col-md-8 br-8">
              <h3 className="pt-1 roboto-new fs-22" style={{ color: "#101828" }}>
                {addressId ? "Edit Office Address" : "Add Office Address"}
              </h3>
              <p className="roboto-new-Paragraph fs-17">
                {addressId ? "Update this office address" : "Create a new office address"}
              </p>
              <div className="card-body box-shadow-datatables mb-5" style={{ borderRadius: "17px" }}>
                <div className="p-2 px-4 mt-1">
                  <AddressForm
                    formik={formik as any}
                    googleMapsApiKey={googleMapsApiKey}
                    onCancel={() => navigate(-1)}
                    submitLabel={addressId ? "Save" : "Add Address"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
}
